/* ============================================================
   ROBA LUCA — eigen gedrag bovenop Zenith
   Ingeladen met defer vanuit layout/theme.liquid.

   Verhuisd uit Custom Liquid-secties op 19/09/2026.
   Achtergrond en meetwaarden: docs/shop/theme-custom-code.md
   ============================================================ */
(() => {
  'use strict';

  /* ----------------------------------------------------------
     0. De stickyheader alvast op de juiste stand zetten

     Dit staat vooraan omdat het over de allééreerste verfbeurt gaat.

     🔴 Wat Lucas op 21/09 filmde: ververs je een pagina met een
     hero terwijl je niet bovenaan staat, dan zie je een tel lang de
     doorzichtige header over de foto voor hij naar wit klapt.

     Het is een volgordekwestie, geen stijlfout. Zeniths eigen
     overlayregel staat in een <style> in de pagina en luidt, kort:

       body:is(body).rl-header-overlay (vier keer de klasse)
         .shopify-section-header:not(.shopify-section-header-sticky):not(.not-mode)
         { background: transparent; --color-foreground: 255,255,255; ... }

     (Tot 07/10/2026 stond daar body:has(main > .has-header-
     interaction:first-child ...); nu zet de server die klasse, zie
     layout/theme.liquid en §96.)

     Die klopt bij de eerste verfbeurt op één ding na:
     .shopify-section-header-sticky. Die klasse zet Zeniths
     sticky-header-component uitsluitend in zijn scroll-handler, en er
     is geen aanroep bij het laden. Bij een verversing herstelt de
     browser je scrollpositie á de eerste verfbeurt -- dus die handler
     draait te laat, en het eerste frame denkt dat je bovenaan staat.
     Datzelfde geldt voor onze eigen regels in rl-header.css en
     rl-hero.css: die hangen aan dezelfde klasse.

     ⚠️ Wij overschrijven hier niets van Zenith. We doen één keer
     dezelfde test die de component zelf doet, op het moment dat de
     scroll al bekend is:

       headerIsAlwaysSticky && scrollTop > header.offsetHeight + 1

     De drempel, de klassenaam en het element komen dus van Zenith, en
     zodra het eerste echte scroll-event komt neemt de component het
     gewoon weer over. Alleen bij data-sticky-type="always", want
     alleen dan werkt de component zo -- staat die instelling op iets
     anders, dan blijven we eraf.

     Nagemeten op 21/09, homepage op scroll 300, verversen: dit script
     is defer en draait op ~426 ms terwijl window.scrollY al 436 is;
     first paint volgt op 564 ms. Ruim honderd milliseconde marge.

     Zie docs/shop/theme-custom-code.md §23.
     ---------------------------------------------------------- */
  const stickyVooraf = () => {
    const wikkel = document.querySelector('sticky-header[data-sticky-type="always"]');
    if (!wikkel) return;
    const sectie = wikkel.closest('.shopify-section-header');
    if (!sectie) return;
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    if (y > sectie.offsetHeight + 1) {
      sectie.classList.add('shopify-section-header-sticky');
    }
  };

  stickyVooraf();

  /* ----------------------------------------------------------
     1. Sliders: Zeniths twee kolommen op telefoon weghalen

     De instellingen van alle sliders (snelheid, 1,2 kaart per
     scherm, native scroll op een toestel zonder muis) staan sinds
     05/10/2026 in assets/rl-swiper.js. Dat bestand moet vóór
     global.js laden en kan dus niet hier staan.
     Zie docs/shop/theme-custom-code.md §49 en §84.

     Hier blijft alleen wat geen Swiper-instelling is. Zenith
     forceert op mobiel twee kolommen met een !important op
     .popular-products__list--mobile-2, en dat verslaat zelfs de
     breedte die Swiper zelf op de kaarten zet. Die klasse moet dus
     weg; daarna rekent Swiper opnieuw.
     ---------------------------------------------------------- */
  const applySlider = () => {
    if (!matchMedia('(max-width: 575px)').matches) return;

    document.querySelectorAll('.popular-products__list').forEach((list) => {
      const weg = [...list.classList].filter((c) => /^popular-products__list--mobile-\d$/.test(c));
      if (!weg.length) return;
      weg.forEach((c) => list.classList.remove(c));
      const slider = list.closest('.popular-products__layout');
      if (slider && slider.swiper) slider.swiper.update();
    });
  };

  /* ----------------------------------------------------------
     2. Galerij-voortgangsindicator op de productpagina

     Desktop leest de scrollpositie binnen de beeldkolom.
     Mobiel leest de actieve slide uit de DOM, niet uit swiper.on():
     de Swiper-instantie kan wisselen. Ze wordt opgeruimd en opnieuw
     gemaakt bij het overschrijden van 990 px, en na een variantwissel
     waarbij Zenith de galerij vervangt is het een nieuwe. Een listener
     op de instantie gaat dan verloren; de DOM blijft kloppen.
     (Tot 04/10 maakte Zenith er bij elke maatverandering van de
     sectie zelfs een extra bij. Dat is opgelost, zie §76.)
     Met een muis staat loop aan vanaf drie beelden (zonder muis
     niet, §84), dus de index komt uit data-media-id tegen een
     ontdubbelde lijst, niet uit activeIndex.
     ---------------------------------------------------------- */
  const initGalleryProgress = () => {
    const media = document.querySelector('.product .product__media');
    if (!media || media.dataset.rlGp) return;

    const mediaIds = () => [...new Set(
      [...media.querySelectorAll('.product__media-item[data-media-id]')].map((e) => e.dataset.mediaId)
    )];
    let ids = mediaIds();
    if (!media.querySelector('.product__media-list') || ids.length < 2) return;   // één beeld: geen indicator
    media.dataset.rlGp = '1';

    const make = (modifier) => {
      const el = document.createElement('div');
      el.className = `rl-gp ${modifier}`;
      el.setAttribute('aria-hidden', 'true');
      el.appendChild(document.createElement('span')).className = 'rl-gp__bar';
      el.style.setProperty('--rl-gp-count', ids.length);
      return el;
    };

    const rail = make('rl-gp--v');
    document.body.appendChild(rail);

    // Hoogte van de laatste foto. Het spoor loopt daar nog een stuk in
    // mee voor het stopt; hoe ver staat in de CSS (--rl-gp-stop).
    // Alleen opnieuw meten wanneer de kolom van hoogte verandert: dat
    // is het enige wat de foto's van formaat doet wisselen (venster,
    // beeld dat inlaadt, swiper die opgebouwd of opgeruimd wordt).
    const laatsteHoogte = () => {
      const beelden = media.querySelectorAll('.product__media-item');
      for (let n = beelden.length - 1; n >= 0; n--) {
        const hoogte = beelden[n].getBoundingClientRect().height;
        if (hoogte > 0) return hoogte;
      }
      return 0;
    };
    let kolomHoogte = -1;

    let frame;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const rect = media.getBoundingClientRect();
        const span = rect.height - innerHeight;
        const visible = span > 8 && rect.bottom > 0 && rect.top < innerHeight;
        // De randen van de beeldkolom, t.o.v. het venster. De CSS klemt
        // het spoor daartussen (rl-custom.css §5), zodat het nooit over
        // de sectie onder de foto's komt te staan. Hier staat alleen de
        // meting, geen maatvoering.
        rail.style.setProperty('--rl-gp-top', `${rect.top}px`);
        rail.style.setProperty('--rl-gp-bottom', `${rect.bottom}px`);
        if (rect.height !== kolomHoogte) {
          kolomHoogte = rect.height;
          rail.style.setProperty('--rl-gp-laatste', `${laatsteHoogte()}px`);
        }
        rail.style.setProperty('--rl-gp-p', visible ? Math.min(1, Math.max(0, -rect.top / span)) : 0);
        rail.dataset.on = visible ? '1' : '0';
      });
    };

    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);

    // De horizontale balk (mobiel) zit ín de galerij. Bij een andere
    // variant vervangt Zenith de hele inhoud van .product__media (de
    // instelling "hide unselected variant media"), en dan verdween de
    // balk mee. mountBar zet hem terug en telt de foto's opnieuw: de
    // nieuwe variant kan een ander aantal hebben. Zie §38.
    let bar = null;
    let slideObserver = null;

    const syncSlide = () => {
      if (!bar) return;
      const active = media.querySelector('.product__media-item.swiper-slide-active');
      const index = active ? ids.indexOf(active.dataset.mediaId) : -1;
      bar.style.setProperty('--rl-gp-i', index < 0 ? 0 : index);
    };

    const mountBar = () => {
      const list = media.querySelector('.product__media-list');
      ids = mediaIds();
      rail.style.setProperty('--rl-gp-count', ids.length);
      kolomHoogte = -1;
      onScroll();
      slideObserver?.disconnect();
      bar = null;
      if (!list || ids.length < 2) return;

      bar = make('rl-gp--h');
      // Na Zeniths zoomcursor, niet ertussen. De CSS van Zenith toont die
      // cursor met .product__media-list.cursor-active + .product__media-cursor,
      // dus hij moet direct na de lijst blijven staan. Stond de balk
      // ertussen, dan verdween je muis boven de foto's (24/09, §32).
      const cursor = list.nextElementSibling?.matches('.product__media-cursor')
        ? list.nextElementSibling
        : list;
      cursor.insertAdjacentElement('afterend', bar);

      slideObserver = new MutationObserver(syncSlide);
      slideObserver.observe(list, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      syncSlide();
    };

    mountBar();

    // Alleen de directe kinderen van .product__media: dat is precies wat
    // Zenith vervangt. De balk zelf staat dieper, dus die telt niet mee.
    new MutationObserver(() => {
      if (!bar || !bar.isConnected) mountBar();
    }).observe(media, { childList: true });
  };

  /* ----------------------------------------------------------
     3. Footer die achter de pagina blijft staan

     De CSS doet het werk (rl-custom.css §6). Hier staan de drie
     dingen die CSS niet kan weten of niet kan doen.

     EEN. Welke secties niet mee vastgezet worden. De wikkel zit om
     de hele footergroep, want die groep verschijnt op élke pagina —
     zet je een sectie in een template, dan moet je hem overal apart
     toevoegen. Maar de USP-rij hoort mee te scrollen in plaats van
     bloot te komen. CSS kan dat niet: wat in de wikkel zit, wordt
     mee vastgezet. Dus verhuizen we die secties naar net vóór de
     wikkel. Ze blijven in de footergroep staan, blijven op elke
     pagina verschijnen en blijven gewoon bewerkbaar in de editor.

     TWEE. Of het effect op dit scherm kan. Alleen als de footer
     volledig in beeld past; anders zie je het onderste stuk nooit,
     want hij schuift niet mee.

     DRIE. Tegen welke hoogte we dat afmeten. Niet innerHeight: op
     een telefoon verschuift die met de adresbalk mee, en dan zou
     het effect tijdens het scrollen aan en uit springen. We meten
     100svh — de kleine viewport, dus met de adresbalk zichtbaar.
     Zenith gebruikt diezelfde eenheid in zijn hero.

     Geen breedtegrens meer: past hij, dan werkt het, ook op mobiel.
     De klasse staat op <html> en niet op body: die bestaat al voor
     de rest van de pagina er is, en zo blijven we van Zeniths eigen
     body-klassen af.
     ---------------------------------------------------------- */
  const FOOTER_MARGE = 40;                        // altijd een streepje pagina zichtbaar laten
  const FOOTER_MEESCROLLEN = '.multicolumn-section'; // secties die niet blootgelegd worden

  let footerUpdate = null;

  const kleineVensterhoogte = () => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100svh;pointer-events:none;visibility:hidden';
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    probe.remove();
    return height > 0 ? height : innerHeight;
  };

  const initFooterReveal = () => {
    const wrapper = document.querySelector('.rl-footer-reveal');
    if (!wrapper) return;

    // Secties die mee moeten scrollen uit de wikkel halen, in volgorde.
    wrapper.querySelectorAll(`:scope > ${FOOTER_MEESCROLLEN}`).forEach((section) => {
      section.classList.add('rl-footer-above');
      wrapper.before(section);
    });

    if (footerUpdate) {
      footerUpdate();
      return;
    }

    const update = () => {
      const height = Math.round(wrapper.getBoundingClientRect().height);
      const fits = height > 0 && height + FOOTER_MARGE <= kleineVensterhoogte();
      document.documentElement.classList.toggle('rl-reveal', fits);
    };

    update();
    if (window.ResizeObserver) new ResizeObserver(update).observe(wrapper);
    addEventListener('resize', update);
    footerUpdate = update;
  };

  /* ----------------------------------------------------------
     4. Footer: twee onderdelen naar een andere kolom, alleen breed

     Zenith zet de sociale iconen vast in het contactblok en de
     land- en taalkiezer in de onderste balk. Er is geen instelling
     om dat te verplaatsen, en CSS kan een element niet naar een
     andere ouder verhuizen. Dus doen we het hier, net als bij de
     USP-rij in §3 — dat scheelt ons een vierde Zenith-bestand in
     beheer.

     Alleen vanaf 750 px, waar de menukolommen naast elkaar staan
     (28/09/2026, theme-custom-code.md §57). Daaronder zijn de
     menukolommen uitklapblokken, en dan hingen de iconen onder het
     eerste blok alsof ze erbij hoorden. Op telefoon blijft alles
     waar Zenith het zet: iconen onder "Get in touch", landenkiezer
     in de onderste balk. De grens is dezelfde 750 px waarop
     footer.liquid zelf van uitklapblok naar kolom wisselt. Wie het
     venster over die grens sleept, krijgt ze mee terug.

     Spacing hoeft niet: alle footerblokken zijn flexkolommen met
     gap: 12px, dus een verplaatst kind krijgt automatisch het
     ritme van het thema.

     Zonder JS blijft alles staan waar Zenith het zet. Dat is een
     nette terugval, en de noscript-variant van de landenkiezer
     staat sowieso in de onderste balk.
     ---------------------------------------------------------- */
  const initFooterLayout = () => {
    const footer = document.querySelector('.footer');
    if (!footer || footer.dataset.rlFooter) return;

    // Wat verhuist, waarheen, en waar Zenith het zelf zette.
    const stukken = [];
    const onthoud = (el, doel) => {
      stukken.push({ el, doel, thuis: el.parentNode, na: el.nextSibling });
    };

    // Sociale iconen onder de eerste menukolom: het merkblok.
    const socials = footer.querySelector('.footer-contact-block .footer-socials');
    const firstColumn = footer.querySelector('.footer-menu-blocks > .footer-menu-block');
    if (socials && firstColumn) onthoud(socials, firstColumn);

    // Land- en taalkiezer onder de nieuwsbriefkolom.
    const subscribe = footer.querySelector('.footer-subscribe-block');
    if (subscribe) {
      footer.querySelectorAll('.footer-bottom .footer-modal').forEach((modal) => onthoud(modal, subscribe));
    }

    const breed = window.matchMedia('(min-width: 750px)');
    const plaats = () => {
      stukken.forEach(({ el, doel, thuis, na }) => {
        if (breed.matches) {
          el.classList.add('rl-footer-moved');
          doel.append(el);
        } else {
          el.classList.remove('rl-footer-moved');
          thuis.insertBefore(el, na && na.parentNode === thuis ? na : null);
        }
      });
      if (footerLijnUpdate) footerLijnUpdate();
    };

    plaats();
    if (breed.addEventListener) breed.addEventListener('change', plaats);
    footer.dataset.rlFooter = '1';
  };

  /* ----------------------------------------------------------
     4b. Footer: die twee items op één lijn, niet helemaal onderaan

     Ze hangen met margin-top: auto onderaan hun kolom (zie
     rl-custom.css §7). Dat zet ze op één lijn, maar wel heel laag:
     de rijhoogte komt van het contactblok met het logo, en dat is
     ruim honderd pixels onder de tekst erboven.

     CSS kan hier niet aan rekenen. De twee zitten op verschillende
     dieptes in twee verschillende kolommen, dus er is geen
     gedeelde flex- of gridlijn om ze aan op te hangen. Meten dus:
     we zoeken de laagste van de twee tekstkolommen en tillen ze
     allebei op tot TUSSENRUIMTE daaronder.

     Het resultaat gaat in een variabele, niet op het element zelf.
     Zo blijft de stijl in de CSS staan.

     Alleen als de rij echt naast elkaar staat. Op mobiel klapt
     Zenith de kolommen onder elkaar en geldt zijn eigen ritme van
     12px; dat laten we met rust.
     ---------------------------------------------------------- */
  const TUSSENRUIMTE = 32;
  let footerLijnUpdate = null;

  const initFooterLijn = () => {
    if (footerLijnUpdate) {
      footerLijnUpdate();
      return;
    }

    const update = () => {
      const footer = document.querySelector('.footer');
      if (!footer) return;

      // eerst terug naar nul, anders meten we onze eigen correctie mee
      footer.style.removeProperty('--rl-footer-optil');

      const rij = footer.querySelector('.footer-main-wrapper');
      const items = Array.prototype.slice.call(footer.querySelectorAll('.rl-footer-moved'));
      if (!rij || items.length < 2) return;
      if (getComputedStyle(rij).flexDirection !== 'row') return;

      let onderkantTekst = 0;
      let onderkantKolom = Infinity;
      let hoogte = 0;

      items.forEach((item) => {
        const vorige = item.previousElementSibling;
        if (vorige) onderkantTekst = Math.max(onderkantTekst, vorige.getBoundingClientRect().bottom);
        const vak = item.getBoundingClientRect();
        onderkantKolom = Math.min(onderkantKolom, vak.bottom);
        hoogte = Math.max(hoogte, vak.height);
      });

      const optil = Math.round(onderkantKolom - hoogte - onderkantTekst - TUSSENRUIMTE);
      if (optil > 0) footer.style.setProperty('--rl-footer-optil', optil + 'px');
    };

    update();
    addEventListener('resize', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    footerLijnUpdate = update;
  };

  /* ----------------------------------------------------------
     5. Hoogte van de sticky header als variabele

     Zenith hééft een --header-height, maar die staat op 0px zolang
     de header niet in overlaystand hangt — nagemeten op de
     FAQ-pagina: variabele 0, header 68 px. Voor alles wat onder de
     header moet blijven plakken (nu de FAQ-kolom) meten we hem dus
     zelf en zetten we het resultaat op <html>.

     Eigen naam, niet Zeniths variabele overschrijven: die wordt
     elders in het thema gelezen en daar willen we niet aan zitten.
     Valt de JS weg, dan staat er in rl-faq.css een terugval.
     ---------------------------------------------------------- */
  const meetHeaderHoogte = () => {
    const header = document.querySelector('.shopify-section-header');
    if (!header) return;
    const hoogte = Math.round(header.getBoundingClientRect().height);
    if (hoogte > 0) document.documentElement.style.setProperty('--rl-header-height', hoogte + 'px');
  };

  /* ----------------------------------------------------------
     6. Maattabel: centimeter of inch

     De cijfers staan in centimeter in de HTML, met de waarde ook
     als data-rl-cm op de cel. Omrekenen gebeurt dus op de cel zelf
     en niet op een tweede, verborgen tabel: één bron, geen kans
     dat de twee uit elkaar lopen.

     Het rekent op elk getal in de cel, niet op de cel als geheel.
     Zo gaat een bereik als 68–96 ook mee: dat wordt 26.8–37.8.

     Zonder JS blijft de balk verborgen (Zeniths no-js-hidden) en
     staat er gewoon een tabel in centimeter. Zie rl-size-guide.css.
     ---------------------------------------------------------- */
  const initMaattabel = () => {
    document.querySelectorAll('.rl-size-guide').forEach((gids) => {
      if (gids.dataset.rlSg) return;

      const cellen = gids.querySelectorAll('[data-rl-cm]');
      const knoppen = gids.querySelectorAll('[data-rl-unit]');
      if (!cellen.length || !knoppen.length) return;
      gids.dataset.rlSg = '1';

      const toon = (eenheid) => {
        cellen.forEach((cel) => {
          const cm = cel.dataset.rlCm;
          cel.textContent = eenheid === 'in'
            ? cm.replace(/[\d.]+/g, (getal) => (parseFloat(getal) / 2.54).toFixed(1))
            : cm;
        });
        knoppen.forEach((knop) => {
          const actief = knop.dataset.rlUnit === eenheid;
          knop.classList.toggle('is-active', actief);
          knop.setAttribute('aria-pressed', actief ? 'true' : 'false');
        });
      };

      knoppen.forEach((knop) => {
        knop.addEventListener('click', () => toon(knop.dataset.rlUnit));
      });
    });
  };

  /* ----------------------------------------------------------
     7. Uitverkocht: de Notify me-knop

     Zenith zet data-status="success" op die knop zodra het
     formulier verstuurd is (assets/avaliability-notification.js),
     en zet hem terug op "false" zodra je een andere maat kiest.
     De knop wordt daardoor groen, maar blijft "Notify me" zeggen --
     dus weet je niet of er iets gebeurd is.

     Wij hangen aan diezelfde data-status. Het label verandert mee,
     en het icoontje ernaast wordt een vinkje. Het icoon zelf staat
     in rl-custom.css §9; hier maken we alleen het lege haakje aan
     waar dat masker in valt, met aria-hidden omdat het decoratief
     is en de knop zijn naam al uit het label haalt.

     Bewust geen tekst vervangen met CSS: data-label voedt Zeniths
     eigen hoveranimatie, dus die moet mee veranderen. Vandaar hier
     en niet daar.

     Zonder JS verandert er niets en staat Zeniths eigen knop er
     gewoon, zoals altijd.
     ---------------------------------------------------------- */
  const NOTIFY_GELUKT = "We'll notify you";

  const initNotify = () => {
    document.querySelectorAll('.avaliability-notification-open-btn').forEach((knop) => {
      if (knop.dataset.rlNotify) return;

      const inhoud = knop.querySelector('.button__content');
      const label = knop.querySelector('.button__label');
      if (!inhoud || !label) return;
      knop.dataset.rlNotify = '1';

      const standaard = label.textContent.trim();

      const icoon = document.createElement('span');
      icoon.className = 'rl-notify-icon';
      icoon.setAttribute('aria-hidden', 'true');
      inhoud.prepend(icoon);

      const update = () => {
        const tekst = knop.dataset.status === 'success' ? NOTIFY_GELUKT : standaard;
        label.textContent = tekst;
        label.dataset.label = tekst;
      };

      new MutationObserver(update).observe(knop, {
        attributes: true,
        attributeFilter: ['data-status'],
      });

      update();
    });
  };

  /* ----------------------------------------------------------
     8. Prijs in de knop van de sticky koopbalk

     De balk toont de prijs links, naast de producttitel, in
     kleine letter. Precies de plek waar je op een telefoon niet
     kijkt terwijl je duim al bij de knop hangt. Dus zetten we hem
     er ook in, achter het label.

     Alles wordt bij elke ronde opnieuw opgezocht en niets wordt
     onthouden: Zenith vervangt bij een variantwissel de hele
     prijsblok en soms ook de knop, dus een bewaarde verwijzing
     wijst daarna naar een element dat niet meer in de pagina
     staat.

     Welke prijs: de zichtbare. In de HTML staan zowel het
     gewone als het afgeprijsde blok; Zenith verbergt er een met
     CSS. offsetParent vertelt welke van de twee echt in beeld is,
     dus dit klopt vanzelf ook op een product in de sale.

     Uitverkocht krijgt geen prijs. Dan staat er "Sold Out" en is
     een bedrag erachter alleen verwarrend.

     Het basislabel komt uit de knop zelf en niet uit een
     constante, want Zenith wisselt tussen Add to cart, Sold Out
     en Pre-order, en die teksten zijn bovendien vertaalbaar.
     Alles voor het scheidingsteken is de basis.

     De bewaking onderaan is wat de lus breekt: onze eigen
     schrijfactie zet de observer opnieuw aan, en die ronde rekent
     dezelfde tekst uit en stopt. Ze kijkt naar allebei de velden,
     niet alleen naar de tekst -- Zenith wisselt bij uitverkocht
     wel het label maar niet data-label, dus bij hover kwam er
     anders "Add to Cart" onder "Sold Out" vandaan.
     ---------------------------------------------------------- */
  const STICKY_SCHEIDING = ' · ';

  const initStickyPrijs = () => {
    const balk = document.querySelector('.product-sticky-add-bar');
    if (!balk || balk.dataset.rlPrijs) return;
    if (!balk.querySelector('.product-form__submit') || !balk.querySelector('.price')) return;
    balk.dataset.rlPrijs = '1';

    const update = () => {
      const knop = balk.querySelector('.product-form__submit');
      const label = knop && knop.querySelector('.button__label');
      const prijsblok = balk.querySelector('.price');
      if (!label || !prijsblok) return;

      const basis = label.textContent.split(STICKY_SCHEIDING)[0].trim();
      const cel = Array.prototype.slice
        .call(prijsblok.querySelectorAll('.price-item'))
        .filter((el) => el.offsetParent !== null && el.textContent.trim())[0];
      const prijs = knop.disabled || !cel ? '' : cel.textContent.trim();
      const tekst = prijs ? basis + STICKY_SCHEIDING + prijs : basis;

      if (label.textContent === tekst && label.dataset.label === tekst) return;
      label.textContent = tekst;
      label.dataset.label = tekst;
    };

    new MutationObserver(update).observe(balk, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class'],
    });

    update();
  };

  /* ----------------------------------------------------------
     9. Uitverkocht: de inschrijving naar Klaviyo

     Zenith laat dit formulier via {% form 'contact' %} naar
     Shopify's contactformulier gaan. Daar komt een mailtje van in
     de winkelinbox en verder gebeurt er niets: niemand krijgt
     bericht als de voorraad terugkomt.

     Wij vangen het versturen af en sturen het naar Klaviyo's
     client-API. Die schrijft de bezoeker in op precies die ene
     variant; de Back in Stock-flow in Klaviyo stuurt daarna
     vanzelf de mail zodra de voorraad boven nul komt.

     Geen enkel Zenith-bestand aangeraakt, en dat kon omdat alles
     wat we nodig hebben al in de pagina staat. Het variant-ID
     zit in het verborgen return_to-veld (Zenith houdt dat zelf
     bij elke maatwissel bij), het e-mailadres in het zichtbare
     veld, en de knop luistert al naar data-status.

     De sleutel hier is de publieke key van Klaviyo. Die hoort in
     de frontend en kan niets lezen -- alleen inschrijven. De
     private key mag hier nooit staan.

     Terugval: lukt het niet om het variant-ID of het adres te
     vinden, dan tonen we onze eigen melding. Vroeger lieten we
     Zenith dan zijn oude ding doen, maar dat levert alleen een
     mail in de winkelinbox en geen inschrijving, en sinds de knop
     geen submit meer afvuurt (zie onder) bestaat die weg niet meer.

     Let op de volgorde bij succes: eerst data-status op de knop,
     dan de lade dicht. Andersom ziet de bezoeker de knop niet
     veranderen.

     🔴 Dit endpoint accepteert maar een handvol profielvelden.
     Nagemeten op 20/09 met een onbestaande variant, zodat er niets
     aangemaakt kon worden:

       email         ✅
       phone_number  ✅ (alleen in E.164, dus +32...)
       first_name    ❌ "is not a valid field for the resource"
       properties    ❌ idem

     Mijn eerste versie stuurde first_name mee zodra het naamveld
     was ingevuld, en de eerste echte test faalde daar dus op --
     niet op de catalogus, wat ik verwachtte. Les: een veld dat een
     API niet kent laat het hele verzoek klappen, ook al is het
     optioneel voor ons.

     Gevolg voor het naamveld: dat gaat hier niet mee. Het staat er
     nog omdat Zenith het rendert; wil je de naam toch in Klaviyo,
     dan is daar een aparte aanroep van /client/profiles/ voor
     nodig ná de inschrijving.

     Het nummer gaat wel mee, zodat het er staat als we later
     WhatsApp of sms willen gebruiken. Maar nooit ten koste van de
     inschrijving: staat het niet in E.164, dan laten we het
     vallen, en weigert Klaviyo het alsnog, dan gaat het verzoek
     automatisch nog één keer zonder nummer. Een optioneel veld
     mag een geslaagde inschrijving niet breken.

     Bewust géén SMS-kanaal en geen marketingtoestemming: het
     nummer opslaan mag, er ongevraagd naartoe berichten niet.
     Dat is een aparte opt-in, later.

     🔴 En het telefoonveld van Zenith staat op pattern="[0-9\-]*"
     -- alleen cijfers en streepjes. Een + wordt door de browser
     geweigerd nog voor er iets verstuurd wordt: "Please match the
     requested format". Voor een Belgische winkel die naar 26 EU-
     landen verzendt is dat onbruikbaar, want +32 is precies hoe je
     een nummer hoort te noteren. Gevonden bij Lucas' eerste test
     op 20/09.

     We verruimen het patroon hier in plaats van in de snippet, om
     dezelfde reden als al de rest: geen vijfde Zenith-bestand in
     beheer. Letters blijven geweigerd, cijfers plus + ( ) . spatie
     en streepje mogen erin. Wat er daarna nog raars in staat vangt
     de normalisatie hierboven op.

     ⚠️ Let op de backslashes in dat patroon, die zijn niet
     overbodig. Browsers compileren het pattern-attribuut met de
     v-vlag, en daarin zijn ( ) en - syntaxtekens die ook bínnen
     een tekenklasse geëscaped moeten worden. Schrijf je
     [0-9+(). -]* dan is de reguliere expressie ongeldig -- en een
     ongeldig patroon wordt door de browser niet gemeld maar
     stilletjes genegeerd, dus dan valideert het veld helemaal
     niets meer. Eerst zo gebouwd op 20/09, betrapt omdat "hallo"
     erdoor kwam.

     🔴 Het e-mailveld heeft dezelfde soort fout, maar erger.
     Zeniths patroon laat alleen kleine letters toe, dus
     Lucas.Verhoest@icloud.com wordt geweigerd. Een hoofdletter in
     een adres is volkomen normaal en volgens de standaard geldig;
     dit zou op launchdag stil inschrijvingen kosten van iedereen
     die zijn adres met een hoofdletter typt. Nagemeten op 20/09.
     Verruimd naar A-Z, met de eis van een echte TLD erin, zodat
     a@b fout blijft -- dat is bijna altijd een typfout.

     De meldingen zelf komen van ons. De browser zegt standaard
     "Please match the requested format", en dat vertelt niemand
     wat er dan mis is. setCustomValidity vervangt die tekst, maar
     alleen bij een patroonfout: een leeg verplicht veld houdt de
     eigen melding van de browser, want die is prima. Eerst
     leegmaken en dan pas opnieuw beoordelen, anders blijft een
     veld voor altijd ongeldig zodra het één keer fout stond.

     Faalt het versturen zelf, dan hangt de melding ervan af wie er
     zweeg. Antwoordde Klaviyo met een fout, dan konden we het adres
     niet bewaren en geven we hello@robaluca.com als tweede weg.
     Kwam er helemaal geen antwoord, dan ligt het aan de verbinding
     van de bezoeker en heeft "probeer opnieuw" pas zin nadat ze
     haar netwerk nakijkt. Vandaar twee teksten, en de markering
     rlAntwoord op de fout om ze uit elkaar te houden.

     🔴 preventDefault is hier niet genoeg. Shopify hangt zelf
     een spamcontrole aan elk {% form 'contact' %}, en die verstuurt
     het formulier soms alsnog -- ook als wij de gebeurtenis al
     geannuleerd hebben. De bezoeker belandt dan op /challenge, een
     captchapagina, en van de inschrijving komt niets terecht.
     Gemeten op 20/09: drie van de zeven pogingen, met exact
     dezelfde handelingen. Niet te voorspellen dus, en niet weg te
     testen. In de logregel zie je het aan prevented: true terwijl
     de pagina tocht naar /challenge springt.

     Daarom vuurt er hier helemaal geen submit meer af. De knop
     wordt een gewone knop (type="button"), wij roepen zelf
     reportValidity aan -- zo houden we de eigen meldingen van de
     browser, inclusief die van hierboven -- en Enter in een veld
     loopt langs dezelfde weg. Wat er niet is, kan Shopify ook niet
     overnemen. De submit-vanger onderaan is alleen nog een net.

     De terugval zonder JS blijft heel: in de HTML staat de knop als
     type="submit" en pas hier zetten we hem om.

     ⚠️ Eén schakel is hier niet vooraf te controleren: of
     Klaviyo's catalogus die variant kent. Zijn catalogus is via
     de API alleen uit te lezen voor custom feeds, niet voor de
     Shopify-sync, dus dat blijkt pas bij de eerste echte
     inschrijving. Faalt het, dan staat de reden van Klaviyo in de
     console en is het wachtwoord op de winkel de eerste
     verdachte.

     (Eerder stond hier dat de producten gedepubliceerd waren.
     Fout gelezen: onlineStoreUrl geeft null zolang er een
     wachtwoord op de winkel staat, ook als het product wél
     gepubliceerd is. resourcePublicationsV2 is het veld dat
     daar wel eenduidig over is.)
     ---------------------------------------------------------- */
  const KLAVIYO_ID = 'Tm6aBW';          // publieke key, mag in de frontend
  const KLAVIYO_REVISIE = '2024-10-15';
  const BIS_FOUT = "We couldn't save your email. Try again, or send it to hello@robaluca.com.";
  const BIS_NET_FOUT = 'No connection. Check your network and try again.';
  const TEL_FOUT = 'Numbers, spaces, and + ( ) - only.';
  const MAIL_FOUT = 'That does not look like an email address.';

  const initBackInStock = () => {
    const lade = document.querySelector('avaliability-notification-drawer');
    const formulier = lade && lade.querySelector('form');
    if (!formulier || formulier.dataset.rlBis) return;
    formulier.dataset.rlBis = '1';

    const veld = (naam) => formulier.querySelector('#AvaliabilityNotification-' + naam);

    const eigenMelding = (element, tekst) => {
      if (!element) return;
      const zet = () => {
        element.setCustomValidity('');
        if (element.validity.patternMismatch || element.validity.typeMismatch) {
          element.setCustomValidity(tekst);
        }
      };
      element.addEventListener('input', zet);
      zet();
    };

    const telefoonveld = veld('Phone');
    if (telefoonveld) telefoonveld.setAttribute('pattern', '[0-9\\s+.\\(\\)\\-]*');
    eigenMelding(telefoonveld, TEL_FOUT);

    const emailveld = veld('Email');
    if (emailveld) {
      emailveld.setAttribute('pattern', '[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}');
    }
    eigenMelding(emailveld, MAIL_FOUT);

    const variantId = () => {
      const retour = veld('return');
      const gevonden = retour && retour.value.match(/variant=(\d+)/);
      return gevonden ? gevonden[1] : null;
    };

    const toonFout = (tekst) => {
      let regel = formulier.querySelector('.rl-bis-melding');
      if (!regel) {
        regel = document.createElement('small');
        regel.className = 'rl-bis-melding form__message form__message--error';
        formulier.append(regel);
      }
      regel.textContent = tekst;
    };

    const verstuur = () => {
      const id = variantId();
      const email = veld('Email');

      const verzendknop = formulier.querySelector('[data-rl-verzend]');
      const naam = veld('Name');
      const oude = formulier.querySelector('.rl-bis-melding');
      if (oude) oude.remove();

      if (!id || !email || !email.value.trim()) {
        toonFout(BIS_FOUT);
        return;
      }

      if (verzendknop) verzendknop.disabled = true;

      // Alleen email en phone_number: eender welk ander veld laat
      // het hele verzoek klappen. Zie de uitleg hierboven.
      const profiel = { email: email.value.trim() };

      const telefoon = veld('Phone');
      const ruw = telefoon ? telefoon.value.trim() : '';
      if (ruw) {
        // \s dekt ook de harde spatie die uit een kopieerplak komt
        const opgekuist = ruw.replace(/[\s.\-()]/g, '').replace(/^00/, '+');
        if (/^\+[1-9]\d{7,14}$/.test(opgekuist)) profiel.phone_number = opgekuist;
      }

      const versturen = (metNummer) => {
        const attributen = Object.assign({}, profiel);
        if (!metNummer) delete attributen.phone_number;

        return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_ID, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISIE },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: attributen } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + id } },
              },
            },
          }),
        }).then((antwoord) => {
          if (antwoord.ok) return null;
          return antwoord.text().then((tekst) => {
            // Ligt het aan het nummer? Dan nog één keer zonder, en het
            // adres raakt alsnog ingeschreven.
            if (metNummer && attributen.phone_number && /phone/i.test(tekst)) {
              console.warn('[RL] nummer geweigerd, opnieuw zonder:', tekst);
              return versturen(false);
            }
            // gemarkeerd: de catch weet zo dat Klaviyo geantwoord heeft
            // en het dus geen netwerkprobleem is
            const fout = new Error(antwoord.status + ' ' + tekst);
            fout.rlAntwoord = true;
            throw fout;
          });
        });
      };

      versturen(true)
        .then(() => {
          const opener = document.querySelector('.avaliability-notification-open-btn');
          if (opener) opener.dataset.status = 'success';
          if (typeof lade.hide === 'function') lade.hide();
          [naam, veld('Phone'), email].forEach((v) => {
            if (v) v.value = '';   // bewust niet form.reset(): dat wist ook de verborgen velden
          });
        })
        .catch((reden) => {
          console.warn('[RL] back-in-stock mislukt:', reden.message);
          toonFout(reden.rlAntwoord ? BIS_FOUT : BIS_NET_FOUT);
        })
        .then(() => {
          if (verzendknop) verzendknop.disabled = false;
        });
    };

    const knop = formulier.querySelector('[type="submit"]');
    if (knop) {
      knop.dataset.rlVerzend = '1';   // straks vindt [type="submit"] hem niet meer
      knop.type = 'button';
      knop.addEventListener('click', () => {
        if (formulier.reportValidity()) verstuur();
      });
    }

    formulier.addEventListener('keydown', (gebeurtenis) => {
      if (gebeurtenis.key !== 'Enter' || gebeurtenis.target.tagName !== 'INPUT') return;
      gebeurtenis.preventDefault();
      if (formulier.reportValidity()) verstuur();
    });

    // Het net: ontstaat er ergens toch nog een submit, dan stopt hij
    // hier en bereikt hij Shopify's spamcontrole niet.
    formulier.addEventListener('submit', (gebeurtenis) => {
      gebeurtenis.preventDefault();
      gebeurtenis.stopImmediatePropagation();
    }, true);
  };

  /* ----------------------------------------------------------
     10. Accordeons op de productpagina: vervallen op 04/10/2026

     Hier stond een omweg voor accordeons die open en meteen weer
     dicht gingen (Zenith hing er bij elke variantwissel een extra
     klikluisteraar bij). Die is sinds 04/10 bij de bron opgelost,
     in initProductAccordion in main-product.js. Uitleg en de oude
     code: docs/shop/theme-custom-code.md §77 en het archief.
     ---------------------------------------------------------- */

  /* ----------------------------------------------------------
     10b. Kleurvierkantjes op de kaart: wisselen bij hover

     27/09/2026, docs/shop/theme-custom-code.md §42. Lucas wilde dat
     de kaart van kleur wisselt als je met de muis over een vierkantje
     gaat, niet pas bij een klik.

     Het wisselen zelf doen we niet. Dat doet Zenith al bij een klik
     (ProductCard._onLinkedProductClick in global.js): foto, titel,
     link, prijs, badges en snel toevoegen gaan naar die kleur. Wij
     geven alleen die klik, zodra de muis even op een vierkantje
     blijft. Blijf je op de kaart en klik je daarna op de foto, dan
     kom je op die kleur uit.

     Verlaat de muis de kaart, dan springt ze terug naar haar eigen
     kleur (03/10/2026, §73). Anders bleef een kleur staan waar de
     muis toevallig even op lag, en stond hetzelfde stuk in dezelfde
     kleur twee keer in een rij (Lucas: twee keer Lizzy Brown in
     "You May Be Interested"). Klik je zelf op een vierkantje, dan
     is dat een keuze en blijft die kleur staan. Het terugspringen
     doet Zeniths eigen kaartwissel (swapLinkedProduct), met de
     overgang uit 10c.

     Zonder JavaScript kan hover enkel een voorproef zijn die terug-
     springt zodra de muis weg is. Dan ziet de klant Beige, klikt op
     de foto en landt op Navy. Daarom deze paar regels.

     - Alleen voor een echte muis (pointerType 'mouse'). Op telefoon
       wisselt een tik, en dat is gewoon Zeniths klik.
     - 100 ms wachten, zodat een muis die er toevallig overheen
       schuift naar de koopknop niets verandert.
     - Alleen op kaarten waar Zeniths kaartwissel aanstaat
       (data-linked-products="true"). Elders is het vierkantje een
       gewone link, en dan zou een klik de pagina openen.
     - Na de wissel vervangt Zenith de rij vierkantjes. Het nieuwe
       vierkantje onder de muis is dan het actieve, en daar gebeurt
       niets meer mee.
     - Eén keer aanhangen op document, bewaakt met een vlag op
       <html>, want start() draait vaker.
     ---------------------------------------------------------- */
  const KAART_HOVER_WACHT = 100;

  // Per kaart: de eigen kleur (standaard het product waarmee de kaart
  // getekend werd, na een echte klik die kleur) en de kleur die de
  // hover nu toont. Een WeakMap ruimt zichzelf op met de kaart.
  const kaartEigen = new WeakMap();
  const kaartHover = new WeakMap();

  const initKaartHover = () => {
    if (document.documentElement.dataset.rlKaartHover) return;
    document.documentElement.dataset.rlKaartHover = '1';

    let wachter = null;
    let bedoeld = null;

    document.addEventListener('pointerover', (gebeurtenis) => {
      if (gebeurtenis.pointerType !== 'mouse') return;
      const doel = gebeurtenis.target;
      const vak = doel && doel.closest ? doel.closest('.rl-card-swatch') : null;
      if (!vak || vak === bedoeld) return;

      clearTimeout(wachter);
      bedoeld = vak;
      if (vak.classList.contains('active')) return;

      const kaart = vak.closest('product-card');
      if (!kaart || kaart.dataset.linkedProducts !== 'true') return;

      wachter = setTimeout(() => {
        // Niet tijdens of net na het slepen van de rij met de muis
        // (rl-swiper.js, §87): dan lag het vierkantje toevallig onder
        // de muis, het was geen hover.
        if (bedoeld === vak && vak.isConnected && vak.matches(':hover') && !vak.closest('.rl-sleept')) {
          kaartHover.set(kaart, vak.dataset.linkedProductId);
          vak.click();
        }
      }, KAART_HOVER_WACHT);
    });

    document.addEventListener('pointerout', (gebeurtenis) => {
      if (gebeurtenis.pointerType !== 'mouse') return;
      const doel = gebeurtenis.target;
      const vak = doel && doel.closest ? doel.closest('.rl-card-swatch') : null;
      if (!vak || vak !== bedoeld) return;
      if (gebeurtenis.relatedTarget && vak.contains(gebeurtenis.relatedTarget)) return;
      clearTimeout(wachter);
      bedoeld = null;
    });

    // Een echte klik of tik op een vierkantje kiest die kleur. Onze
    // eigen klik hierboven is geen echte (isTrusted false).
    document.addEventListener('click', (gebeurtenis) => {
      if (!gebeurtenis.isTrusted) return;
      const doel = gebeurtenis.target;
      const vak = doel && doel.closest ? doel.closest('.rl-card-swatch') : null;
      const kaart = vak ? vak.closest('product-card') : null;
      if (!kaart) return;
      kaartEigen.set(kaart, vak.dataset.linkedProductId);
      kaartHover.delete(kaart);
    }, true);

    // De muis verlaat de kaart: terug naar de eigen kleur.
    document.addEventListener('pointerout', (gebeurtenis) => {
      if (gebeurtenis.pointerType !== 'mouse') return;
      const doel = gebeurtenis.target;
      const kaart = doel && doel.closest ? doel.closest('product-card') : null;
      if (!kaart || !kaartHover.has(kaart)) return;
      if (gebeurtenis.relatedTarget && kaart.contains(gebeurtenis.relatedTarget)) return;

      const getoond = String(kaartHover.get(kaart));
      kaartHover.delete(kaart);
      const eigen = String(kaartEigen.get(kaart) || kaart.dataset.productId);
      if (getoond === eigen) return;

      const data = (kaart.linkedProductsData || []).find((item) => String(item.id) === eigen);
      if (!data || typeof kaart.swapLinkedProduct !== 'function') return;
      // Eén overgang, in het tempo van gewoon van een kaart af gaan.
      kaartOvergang(kaart, () => kaart.swapLinkedProduct(data), KAART_TERUG_FADE, 0);
    });
  };

  /* ----------------------------------------------------------
     10c. Kleurvierkantjes op de kaart: de foto vervaagt naar de
     nieuwe kleur

     02/10/2026, docs/shop/theme-custom-code.md §71. Lucas wilde
     dezelfde overgang als bij de tweede foto (rl-card-hover.css),
     maar sneller.

     Waarom hier en niet in CSS: Zenith wisselt de foto door src en
     srcset van dezelfde <img> te vervangen (updateLinkedImage in
     global.js). Er is dus geen tweede beeld om naar te vervagen, en
     CSS merkt een nieuwe src niet op.

     - Vlak voor Zeniths wissel leggen we een kopie van de foto er
       bovenop. We luisteren in de capture-fase, dus wij zien de klik
       vóór de kaart. De kopie draagt dezelfde klassen en toont dus
       exact wat er stond, ook de tweede foto als de muis op de kaart
       staat. inert: niet klikbaar, niet voor schermlezers.
     - Is de nieuwe foto geladen en gedecodeerd, dan vervaagt de
       kopie in 150 ms en zakt de nieuwe foto van 103 % naar zijn
       eigen stand in 250 ms. Zelfde verhouding en curve als bij de
       tweede foto. Daarna gaat de kopie uit de pagina.
     - Laadt de nieuwe foto traag, dan blijft de oude staan tot de
       nieuwe er is. Zonder dit stond de kaart dan even leeg.
     - Wisselt iemand opnieuw voor de nieuwe foto er is, dan blijft
       dezelfde kopie liggen: die toont de laatste foto die echt in
       beeld stond.
     - Alleen met een muis en zonder "minder beweging", dezelfde
       voorwaarde als rl-card-hover.css. Op telefoon blijft het
       Zeniths gewone wissel.
     - Geldt voor elke wissel: de hover uit 10b geeft ook een klik,
       en het terugspringen uit 10b roept kaartOvergang zelf op.
     - De kopie is een stilstaande foto van wat er op dat moment in
       beeld is, ook midden in de hover-overgang van
       rl-card-hover.css. En die overgang op de echte foto wordt
       meteen afgerond. Zonder dat zag je bij het terugspringen eerst
       de tweede foto van de eigen kleur en pas daarna de eerste
       (03/10/2026, §74).
     - Terugspringen bij het verlaten van de kaart gaat in 350 ms en
       zonder zoom: hetzelfde tempo als gewoon van een kaart af gaan.
     ---------------------------------------------------------- */
  const KAART_OVERGANG_MEDIA = '(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)';
  const KAART_OVERGANG_FADE = 150;
  const KAART_OVERGANG_ZOOM = 250;
  const KAART_OVERGANG_CURVE = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
  const KAART_TERUG_FADE = 350; // zoals de eerste foto in rl-card-hover.css

  const fotoKlaar = (img) => (img.complete
    ? Promise.resolve()
    : new Promise((klaar) => {
      img.addEventListener('load', klaar, { once: true });
      img.addEventListener('error', klaar, { once: true });
    })
  ).then(() => img.decode().catch(() => {}));

  const kaartMuis = matchMedia(KAART_OVERGANG_MEDIA);
  const kaartLopend = new WeakMap();

  // De overgang op één kaart. wissel vervangt de foto; zonder wissel
  // doet Zenith dat zelf, in dezelfde klik (zie initKaartOvergang).
  // fade en zoom in ms; zoom 0 = geen zoom.
  const kaartOvergang = (kaart, wissel, fade = KAART_OVERGANG_FADE, zoom = KAART_OVERGANG_ZOOM) => {
    const foto = kaart.querySelector('.product-card__media');
    if (!foto || !kaartMuis.matches) {
      if (wissel) wissel();
      return;
    }
    const echt = [...foto.querySelectorAll('img')];

    let stand = kaartLopend.get(kaart);
    if (stand && stand.vervaagt) {
      stand.kopie.remove();
      stand = null;
    }
    if (!stand) {
      // De kopie zit in een eigen houder die precies over de echte
      // foto ligt, in elke opbouw van de kaart (05/10/2026, §85). Op
      // desktop staat de foto zelf absoluut over de kaart; in de smalle
      // opbouw (info onder de foto) zet Zenith ze met !important in de
      // stroom. Een kopie zonder houder erfde dat en zakte onder de
      // foto, over de naam en de prijs. In de houder volgt de kopie
      // gewoon Zeniths regels voor de foto, zonder tegen !important in
      // te gaan.
      const kopie = document.createElement('div');
      kopie.className = 'rl-kaart-kopie';
      kopie.inert = true;
      const ouder = foto.offsetParent;
      if (ouder) {
        const f = foto.getBoundingClientRect();
        const o = ouder.getBoundingClientRect();
        Object.assign(kopie.style, {
          position: 'absolute',
          top: `${f.top - o.top - ouder.clientTop}px`,
          left: `${f.left - o.left - ouder.clientLeft}px`,
          width: `${f.width}px`,
          height: `${f.height}px`,
        });
      }
      kopie.append(foto.cloneNode(true));
      // Stilstaand: elke foto in de kopie krijgt de stand die ze nu
      // heeft, ook midden in een hover-overgang.
      kopie.querySelectorAll('img').forEach((img, i) => {
        if (!echt[i]) return;
        const nu = getComputedStyle(echt[i]);
        img.style.transition = 'none';
        img.style.opacity = nu.opacity;
        img.style.transform = nu.transform;
        img.style.visibility = nu.visibility;
      });
      foto.after(kopie);
      stand = { kopie, vervaagt: false };
      kaartLopend.set(kaart, stand);
    }
    const beurt = {};
    stand.beurt = beurt;

    if (wissel) wissel();

    // Na de wissel; bij een klik doet Zenith die nog in dezelfde klik.
    requestAnimationFrame(() => {
      // Een hover-overgang op de echte foto meteen afronden, ook een
      // die net begint omdat de muis de kaart verliet. De kopie dekt
      // dit moment af; daarna vervaagt ze in één keer naar de eindstand.
      const fotos = [...foto.querySelectorAll('img')];
      const vorige = fotos.map((img) => img.style.transition);
      fotos.forEach((img) => { img.style.transition = 'none'; });
      void foto.offsetWidth;
      fotos.forEach((img, i) => { img.style.transition = vorige[i]; });

      Promise.all(fotos.map(fotoKlaar)).then(() => {
        if (stand.beurt !== beurt || stand.vervaagt) return;
        stand.vervaagt = true;

        if (zoom) {
          foto.querySelectorAll('img').forEach((img) => img.animate(
            [{ transform: 'scale(1.03)' }, {}],
            { duration: zoom, easing: KAART_OVERGANG_CURVE }
          ));
        }

        const opruimen = () => {
          stand.kopie.remove();
          if (kaartLopend.get(kaart) === stand) kaartLopend.delete(kaart);
        };
        stand.kopie.animate(
          { opacity: [1, 0] },
          { duration: fade, easing: 'ease', fill: 'forwards' }
        ).finished.then(opruimen, opruimen);
      });
    });
  };

  const initKaartOvergang = () => {
    if (document.documentElement.dataset.rlKaartOvergang) return;
    document.documentElement.dataset.rlKaartOvergang = '1';

    document.addEventListener('click', (gebeurtenis) => {
      if (!kaartMuis.matches) return;
      const doel = gebeurtenis.target;
      const vak = doel && doel.closest ? doel.closest('.rl-card-swatch') : null;
      if (!vak || vak.classList.contains('active')) return;
      const kaart = vak.closest('product-card');
      if (!kaart || kaart.dataset.linkedProducts !== 'true') return;
      kaartOvergang(kaart);
    }, true);
  };

  /* ----------------------------------------------------------
     12. Focuskader alleen voor wie met het toetsenbord werkt

     28/09/2026, theme-custom-code.md §59. Zenith toont het kader
     rond een knop of link enkel bij :focus-visible, en dat is
     goed: wie met Tab door de site gaat, moet zien waar hij is. Maar
     de browser beslist zelf wanneer :focus-visible geldt, en Safari
     (iPhone en Mac) doet dat ook wanneer een script de focus
     verplaatst na een tik of klik. Zenith en wij doen dat bewust,
     voor schermlezers en het toetsenbord: bij het sluiten van de
     lade terug naar de menuknop, bij het sluiten van de zoom terug
     naar de foto, bij het openen van de cookiebanner naar de eerste
     knop. Gevolg: een kader na elke tik.

     CSS kan niet weten of je laatst tikte of een toets gebruikte.
     Dus houden we dat hier bij, als data-rl-invoer op <html>:
     "wijzer" na een tik of klik, "toets" na een navigatietoets.
     rl-custom.css §26 verbergt het kader zolang er "wijzer" staat.

     We beginnen op "wijzer": de cookiebanner zet de focus al bij
     het laden, vóór iemand iets gedaan heeft. De eerste Tab zet het
     kader meteen terug. Spatie, Enter en de pijltjes tellen niet
     mee terwijl je in een tekstveld typt (wel bij maten en andere
     keuzerondjes: daar kies je met de pijltjes).
     ---------------------------------------------------------- */
  const RL_NAVTOETSEN = ['Tab', 'Escape', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];

  const zetInvoer = (soort) => {
    if (document.documentElement.dataset.rlInvoer !== soort) document.documentElement.dataset.rlInvoer = soort;
  };

  const RL_TYPVELDEN = ['text', 'email', 'search', 'tel', 'password', 'number', 'url'];
  const typtInVeld = (el) => !!el && (el.isContentEditable || el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && RL_TYPVELDEN.includes(el.type)));

  zetInvoer('wijzer');
  addEventListener('pointerdown', () => zetInvoer('wijzer'), true);
  addEventListener('keydown', (gebeurtenis) => {
    if (!RL_NAVTOETSEN.includes(gebeurtenis.key)) return;
    if (gebeurtenis.key !== 'Tab' && gebeurtenis.key !== 'Escape' && typtInVeld(gebeurtenis.target)) return;
    zetInvoer('toets');
  }, true);

  /* ----------------------------------------------------------
     13. Schermhoge hero op een aanraakscherm: hoogte vastzetten

     05/10/2026, theme-custom-code.md §88. Lucas filmde het op zijn
     iPhone, buiten Safari: scroll je op de homepage, dan wordt de
     foto in de hero telkens 6 % groter en weer kleiner, en schuift
     alles eronder mee. In PostHog zag hij het ook.

     Zenith maakt een hero, media banner of slideshow op volle
     hoogte even hoog als 100svh: de schermhoogte met de balken van
     de browser zichtbaar. In Safari blijft dat getal vast tijdens
     het scrollen. In Chrome en in de ingebouwde browsers van
     Instagram en Facebook op een iPhone volgt het de hoogte van het
     venster: klapt de balk weg, dan wordt het venster 30 à 45 px
     hoger en het blok groeit mee. De foto vult het blok altijd
     (object-fit: cover), dus ze wordt groter.

     CSS kan dat niet oplossen: in die browsers volgt elke eenheid
     voor de schermhoogte het venster (svh, lvh, dvh en vh). Dus
     meten we bij het laden één keer hoe hoog het blok is en zetten
     we die hoogte vast. Bij het laden staan de balken open, dus dat
     is dezelfde hoogte die Safari gebruikt; in Safari verandert er
     dus niets. Opnieuw meten doen we alleen als de breedte
     verandert (de telefoon draaien), niet als de balk in- of
     uitklapt.

     Alleen op een aanraakscherm. Op een computer verandert de
     hoogte van het venster alleen als iemand het groter of kleiner
     sleept, en dan moet de hero gewoon meegaan.

     clientWidth en niet innerWidth: op een iPhone verandert
     innerWidth ook als je met twee vingers inzoomt.

     Zonder JS blijft Zeniths regel staan: correct, alleen met de
     sprong in die browsers.
     ---------------------------------------------------------- */
  const RL_SCHERMHOOG = (soort) =>
    `.hero__wrapper--full-${soort}, .media-banner__wrapper--full-${soort}, .slideshow__slide--full-${soort}`;

  let schermBreedte = 0;

  const zetSchermhoogteVast = () => {
    if (!matchMedia('(pointer: coarse)').matches) return;
    const breedte = document.documentElement.clientWidth;
    if (breedte === schermBreedte) return;
    schermBreedte = breedte;

    // Eerst loslaten, zodat Zeniths regel voor de nieuwe breedte de hoogte bepaalt.
    document.querySelectorAll('[data-rl-vast]').forEach((vak) => {
      vak.style.removeProperty('min-height');
      delete vak.dataset.rlVast;
    });

    // Zenith wisselt op 576 px tussen de instelling voor mobiel en die voor groter.
    const soort = matchMedia('(max-width: 575.98px)').matches ? 'mobile' : 'desktop';
    const vakken = [...document.querySelectorAll(RL_SCHERMHOOG(soort))];

    // Eerst alles meten, dan alles schrijven: de browser rekent de opmaak zo maar één keer uit.
    const hoogtes = vakken.map((vak) => vak.getBoundingClientRect().height);
    vakken.forEach((vak, i) => {
      // Zeniths regel staat op !important; een inline !important wint daarvan.
      vak.style.setProperty('min-height', `${hoogtes[i]}px`, 'important');
      vak.dataset.rlVast = '';
    });
  };

  /* ----------------------------------------------------------
     14. De doorzichtige header in de thema-editor

     Of de header doorzichtig over de eerste sectie ligt, beslist
     layout/theme.liquid sinds 07/10/2026 op de server, met de
     klasse rl-header-overlay op <body> (§96). In de thema-editor
     verschuift, verbergt of vervangt Shopify secties zonder de
     pagina opnieuw te laden, en dan bleef die klasse staan zoals
     bij het laden: een hero die je bovenaan zet, schoof dan onder
     een witte header tot je de pagina ververste. Daarom zetten we
     hem in de editor, en alleen daar, opnieuw na elke wissel, met
     dezelfde test als de server: de eerste sectie in main heeft
     has-header-interaction en bevat [data-header-interaction].
     Zeniths header.js reageert op dezelfde gebeurtenissen. Voor
     bezoekers van de winkel draait dit nooit.
     ---------------------------------------------------------- */
  const zetHeaderOverlay = () => {
    const main = document.getElementById('MainContent');
    const eerste = main ? main.firstElementChild : null;
    const aan = !!eerste &&
      eerste.classList.contains('has-header-interaction') &&
      !!eerste.querySelector('[data-header-interaction]');
    document.body.classList.toggle('rl-header-overlay', aan);
  };

  /* ----------------------------------------------------------
     15. De chat van Shopify Inbox boven de sticky koopbalk

     Het bolletje van Inbox staat rechtsonder, op de hoogte van de
     sticky Add to Cart van de productpagina, en lag daar over de
     knop. Zenith zet .active op .product-sticky-add-bar zolang de
     balk in beeld is. Wij zetten dan rl-sticky-balk op <body> en
     de hoogte van de balk in --rl-sticky-hoogte; rl-custom.css §36
     schuift het bolletje zo ver omhoog. Een klasse op <body> en
     geen body:has(), zoals bij de header (§96).
     Zonder balk, op de andere pagina's, gebeurt er niets.
     ---------------------------------------------------------- */
  const initChatBovenBalk = () => {
    const balk = document.querySelector('.product-sticky-add-bar');
    if (!balk || balk.dataset.rlChat) return;
    balk.dataset.rlChat = '1';

    const zet = () => {
      const aan = balk.classList.contains('active');
      if (aan) document.documentElement.style.setProperty('--rl-sticky-hoogte', balk.offsetHeight + 'px');
      document.body.classList.toggle('rl-sticky-balk', aan);
    };

    new MutationObserver(zet).observe(balk, { attributes: true, attributeFilter: ['class'] });
    zet();
  };

  /* ----------------------------------------------------------
     11. Aanhaken

     DOMContentLoaded is niet optioneel: dit bestand laadt in de
     head met defer, dus zonder die wacht bestaat .product__media
     nog niet en gebeurt er stilletjes niets.
     shopify:section:load vangt het opnieuw renderen in de editor.
     ---------------------------------------------------------- */
  const start = () => {
    applySlider();
    initGalleryProgress();
    initFooterLayout();
    initFooterLijn();
    initFooterReveal();
    meetHeaderHoogte();
    zetSchermhoogteVast();
    initMaattabel();
    initNotify();
    initStickyPrijs();
    initChatBovenBalk();
    initBackInStock();
    initKaartHover();
    initKaartOvergang();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  addEventListener('load', start);
  addEventListener('resize', applySlider);
  addEventListener('resize', meetHeaderHoogte);
  addEventListener('resize', zetSchermhoogteVast, { passive: true });
  document.addEventListener('shopify:section:load', () => setTimeout(start, 60));
  if (window.Shopify && window.Shopify.designMode) {
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((naam) => {
      document.addEventListener(naam, () => setTimeout(zetHeaderOverlay, 60));
    });
  }
})();

/*
  ROBA LUCA §115 (17/10/2026): een inschrijving voor de nieuwsbrief meteen
  aan Klaviyo doorgeven. docs/shop/theme-custom-code.md §115.

  Waarom. Tot nu ging een inschrijving (popup of footer) enkel naar Shopify.
  Shopify geeft ze via een webhook door aan Klaviyo, en pas dan start de
  welkomstflow. Nu sturen we het adres, nadat Shopify de inschrijving
  aanvaard heeft (dus na Shopify's spamcontrole), ook rechtstreeks naar
  Klaviyo, met de bron in de profieleigenschap "Signup Form" (popup of
  footer). Klaviyo zet het profiel meteen op de Email List en de flow
  Welcome 10% first order (Email) v2 (RVKjwW) vertrekt zonder op Shopify
  te wachten. Zo doet Scuffers het ook.

  Hoe.
  - Bij het versturen van een formulier naar /contact met de tag
    "newsletter" onthoudt sessionStorage het adres en de bron.
  - Gelukt zonder herladen (de popup, §114): popup.js stuurt het event
    rl:nieuwsbrief-ingeschreven.
  - Gelukt met herladen (de footer, of de terugval van de popup): Shopify
    opent de pagina met ?customer_posted=true. We lezen het adres waarmee
    de pagina geladen werd, want popup.js haalt customer_posted daarna uit
    de adresbalk.
  - Pas dan gaat het adres naar Klaviyo, één keer: de sleutel verdwijnt
    vóór het versturen.

  Gaat het mis (geen netwerk, Klaviyo weigert), dan merkt de bezoeker niets:
  de webhook van Shopify brengt de inschrijving zoals vroeger binnen, en de
  flow herkent die aan de Shopify-tag "newsletter".

  Geen cookies: fetch zonder credentials, en klaviyo.js speelt niet mee.
  PostHog (enkel als het geladen is, dus na toestemming):
  rl_nieuwsbrief_klaviyo met bron, gelukt en status.
*/
(function () {
  const SLEUTEL = 'rl-klaviyo-wacht';
  const KLAVIYO_SITE = 'Tm6aBW';
  const KLAVIYO_LIJST = 'VdiNq2';
  const GELDIG_MS = 15 * 60 * 1000;

  const tagsVan = (form) => {
    const veld = form.querySelector('input[name="contact[tags]"]');
    if (!veld) return [];
    return veld.value.split(',').map((tag) => tag.trim());
  };

  const bewaar = (form) => {
    const tags = tagsVan(form);
    if (tags.indexOf('newsletter') === -1) return;
    const veld = form.querySelector('input[name="contact[email]"]');
    const email = veld ? veld.value.trim().toLowerCase() : '';
    if (!email) return;
    const bron = tags.indexOf('rl-popup') !== -1 ? 'popup' : 'footer';
    try {
      window.sessionStorage.setItem(
        SLEUTEL,
        JSON.stringify({ email: email, bron: bron, t: Date.now() })
      );
    } catch (e) {}
  };

  const meet = (data) => {
    try {
      const ph = window.posthog;
      if (!ph || !ph.__loaded || typeof ph.capture !== 'function') return;
      ph.capture('rl_nieuwsbrief_klaviyo', data);
    } catch (e) {}
  };

  const verstuur = () => {
    let wacht = null;
    try {
      wacht = JSON.parse(window.sessionStorage.getItem(SLEUTEL) || 'null');
      window.sessionStorage.removeItem(SLEUTEL);
    } catch (e) {
      return;
    }
    if (!wacht || !wacht.email || !wacht.t) return;
    if (Date.now() - wacht.t > GELDIG_MS) return;
    if (!window.fetch) return;
    const bron = wacht.bron === 'popup' ? 'popup' : 'footer';
    window
      .fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_SITE,
        {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            revision: '2026-01-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'ROBA LUCA ' + bron,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: wacht.email,
                      properties: { 'Signup Form': bron },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: KLAVIYO_LIJST } },
              },
            },
          }),
        }
      )
      .then((antwoord) => {
        meet({ bron: bron, gelukt: antwoord.ok, status: antwoord.status });
      })
      .catch(() => {
        meet({ bron: bron, gelukt: false, status: 'netwerk' });
      });
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/contact"]')) return;
      bewaar(form);
    },
    true
  );

  document.addEventListener('rl:nieuwsbrief-ingeschreven', verstuur);

  let geladenMet = window.location.href;
  try {
    const navigatie = performance.getEntriesByType('navigation')[0];
    if (navigatie && navigatie.name) geladenMet = navigatie.name;
  } catch (e) {}
  if (/[?&]customer_posted=true(&|#|$)/.test(geladenMet)) verstuur();
})();